import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '@/services/authService';
import { apiClient } from '@/utils/api/client';
import { monitoringService } from '@/services/monitoring';
import { userService } from '@/services/userService';

interface User {
  id: string;
  email: string;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isOtpRequested: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  setAuth: (user: User, token: string) => void;
  logout: () => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  requestOtp: (email: string) => Promise<boolean>;
  verifyOtp: (email: string, otp: string) => Promise<boolean>;
  checkAuth: () => Promise<void>;
  setIsOtpRequested: (isRequested: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isOtpRequested: false,
      isLoading: false,
      error: null,

      setAuth: (user, token) => {
        // Set token in API client
        apiClient.setAuthToken(token);

        set({
          user,
          token,
          isAuthenticated: true,
          isOtpRequested: false,
          error: null,
        });
      },

      logout: () => {
        // Clear token in API client
        apiClient.setAuthToken(null);

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isOtpRequested: false,
          error: null,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
      setIsOtpRequested: (isRequested) => set({ isOtpRequested: isRequested }),

      requestOtp: async (email: string) => {
        try {
          set({ isLoading: true, error: null, isOtpRequested: false });
          await authService.sendOtp(email);
          set({ isLoading: false, isOtpRequested: true });
          return true;
        } catch (error) {
          const errorData = error as { data?: { message?: string } };
          set({
            isLoading: false,
            error: errorData.data?.message || 'Failed to send OTP',
            isOtpRequested: false,
          });
          return false;
        }
      },

      verifyOtp: async (email: string, otp: string) => {
        try {
          set({ isLoading: true, error: null });
          const response = await authService.verifyOtp(email, otp);

          if (response.success && response.token && response.userId) {
            get().setAuth(
              { id: response.userId, email: response.email || email },
              response.token
            );
            set({ isLoading: false, isOtpRequested: false });

            // Send clarityId to backend for session linking (non-blocking)
            const clarityId = monitoringService.getClarityId();
            if (clarityId) {
              userService.updateTracking(clarityId).catch((err) => {
                console.error('Failed to update tracking data:', err);
              });
            }

            return true;
          } else {
            set({
              isLoading: false,
              error: response.message || 'Invalid OTP',
            });
            return false;
          }
        } catch (error) {
          const errorData = error as { data?: { message?: string } };
          set({
            isLoading: false,
            error: errorData.data?.message || 'Failed to verify OTP',
          });
          return false;
        }
      },

      checkAuth: async () => {
        const state = get();
        if (state.token) {
          apiClient.setAuthToken(state.token);
          try {
            const status = await authService.checkAuthStatus();
            if (!status.isAuthenticated) {
              state.logout();
            }
          } catch {
            state.logout();
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        isOtpRequested: state.isOtpRequested,
      }),
      onRehydrateStorage: () => (state) => {
        // When storage is rehydrated, set the token in API client
        if (state?.token) {
          apiClient.setAuthToken(state.token);
        }
      },
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          if (!str) return null;
          return JSON.parse(str);
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    }
  )
);

// Initialize auth token on app start
export const initializeAuth = () => {
  const state = useAuthStore.getState();
  if (state.token) {
    apiClient.setAuthToken(state.token);
  }
};

// Run initialization immediately
initializeAuth();